/**
 * Post DSA Offer Page
 * DSAs create and publish their commission rate cards
 * Redesigned to match LeadFlow prototype style
 *
 * Features:
 * - Multi-step form for creating offers
 * - Bank/Lender autocomplete
 * - Commission percentage with 3 decimal precision
 * - Loan amount range configuration
 * - Service area specification
 * - Deadline setting
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { Slider } from 'primereact/slider';
import { Chips } from 'primereact/chips';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { InputTextarea } from 'primereact/inputtextarea';
import { Skeleton } from 'primereact/skeleton';
import { AutoComplete } from 'primereact/autocomplete';
import {
    dsaOfferService,
    DSA_SEGMENTS,
    formatCommission,
    formatLoanRange
} from '../../services/dsaOfferService';
import axiosInstance from '../../config/axios.config';
import NavigationTabMenu from '../../components/common/NavigationTabMenu';

// My Payouts section tabs
const MY_PAYOUTS_TABS = [
    { label: 'My Listed Payouts', icon: 'pi pi-list', route: '/app/dsa-offers/my-offers' },
    { label: 'Payout Enquiries', icon: 'pi pi-inbox', route: '/app/dsa-offers/received-invites' },
    { label: 'Post Payout', icon: 'pi pi-plus', route: '/app/dsa-offers/post' }
];

// Custom styles for the page - Compact design matching PostLead pattern
const pageStyles = `
    .post-offer-page {
        max-width: 1000px;
        margin: 0 auto;
        font-size: 0.8125rem;
    }

    /* Custom Wizard Steps - Compact */
    .post-offer-page .wizard-steps {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0;
        margin-bottom: 1rem;
        padding: 0.75rem;
        background: var(--surface-card);
        border-radius: 10px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    }

    .post-offer-page .wizard-step {
        display: flex;
        align-items: center;
        gap: 0.375rem;
    }

    .post-offer-page .wizard-step-number {
        width: 26px;
        height: 26px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: 0.75rem;
        background: var(--surface-200);
        color: var(--text-color-secondary);
        transition: all 0.3s ease;
    }

    .post-offer-page .wizard-step.active .wizard-step-number {
        background: var(--primary-color);
        color: white;
        box-shadow: 0 2px 6px rgba(99, 102, 241, 0.35);
    }

    .post-offer-page .wizard-step.completed .wizard-step-number {
        background: #22C55E;
        color: white;
    }

    .post-offer-page .wizard-step-label {
        font-size: 0.8125rem;
        font-weight: 500;
        color: var(--text-color-secondary);
    }

    .post-offer-page .wizard-step.active .wizard-step-label,
    .post-offer-page .wizard-step.completed .wizard-step-label {
        color: var(--text-color);
    }

    .post-offer-page .wizard-step-connector {
        width: 40px;
        height: 2px;
        background: var(--surface-200);
        margin: 0 0.375rem;
    }

    .post-offer-page .wizard-step-connector.completed {
        background: #22C55E;
    }

    @media (max-width: 576px) {
        .post-offer-page .wizard-steps {
            padding: 0.5rem;
        }
        .post-offer-page .wizard-step-label {
            display: none;
        }
        .post-offer-page .wizard-step-connector {
            width: 30px;
        }
    }

    /* Form Group - Compact */
    .post-offer-page .form-group {
        margin-bottom: 1rem;
    }

    .post-offer-page .form-label {
        display: block;
        font-weight: 600;
        font-size: 0.75rem;
        color: var(--text-color);
        margin-bottom: 0.25rem;
    }

    .post-offer-page .form-label.required::after {
        content: ' *';
        color: #EF4444;
    }

    .post-offer-page .form-help {
        font-size: 0.6875rem;
        color: var(--text-color-secondary);
        margin-top: 0.25rem;
    }

    /* Validation Error States */
    .post-offer-page .form-error {
        font-size: 0.6875rem;
        color: #EF4444;
        margin-top: 0.25rem;
    }

    .post-offer-page .field-error .p-inputtext,
    .post-offer-page .field-error .p-dropdown,
    .post-offer-page .field-error .p-autocomplete .p-inputtext,
    .post-offer-page .field-error .p-chips .p-chips-multiple-container,
    .post-offer-page .field-error .p-calendar .p-inputtext,
    .post-offer-page .field-error .p-inputnumber .p-inputtext {
        border-color: #EF4444 !important;
    }

    .post-offer-page .field-error .p-inputtext:focus,
    .post-offer-page .field-error .p-dropdown:focus,
    .post-offer-page .field-error .p-dropdown.p-focus {
        box-shadow: 0 0 0 1px rgba(239, 68, 68, 0.3) !important;
        border-color: #EF4444 !important;
    }

    .post-offer-page .field-error .p-checkbox .p-checkbox-box {
        border-color: #EF4444 !important;
    }

    /* Quick Chips - Compact */
    .post-offer-page .quick-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 0.375rem;
    }

    .post-offer-page .quick-chip {
        padding: 0.375rem 0.75rem;
        border-radius: 16px;
        border: 1px solid var(--surface-300);
        background: var(--surface-card);
        color: var(--text-color);
        font-size: 0.8125rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .post-offer-page .quick-chip:hover {
        border-color: var(--primary-color);
        background: var(--primary-50);
    }

    .post-offer-page .quick-chip.active {
        background: var(--primary-color);
        border-color: var(--primary-color);
        color: white;
    }

    /* Summary Sidebar - Compact */
    .post-offer-page .summary-sidebar {
        position: sticky;
        top: 80px;
    }

    .post-offer-page .summary-sidebar h4 {
        font-size: 0.875rem;
    }

    @media (max-width: 991px) {
        .post-offer-page .summary-sidebar {
            position: static;
        }
    }

    /* Card and Section Headers - Compact */
    .post-offer-page h3 {
        font-size: 0.9375rem !important;
    }

    .post-offer-page h4 {
        font-size: 0.8125rem !important;
    }

    /* Reduce overall card padding */
    .post-offer-page .surface-card.p-3 {
        padding: 0.875rem !important;
    }

    /* Tighter section spacing */
    .post-offer-page .mb-4 {
        margin-bottom: 0.875rem !important;
    }

    .post-offer-page .mb-3 {
        margin-bottom: 0.625rem !important;
    }

    /* Compact grid gaps */
    .post-offer-page .gap-3 {
        gap: 0.625rem !important;
    }

    .post-offer-page .gap-2 {
        gap: 0.375rem !important;
    }

    /* Section header with icon */
    .post-offer-page .section-header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 0.75rem;
        background: var(--primary-50);
        border-radius: 6px;
        margin-bottom: 0.75rem;
    }

    .post-offer-page .section-header i {
        color: var(--primary-color);
        font-size: 0.875rem;
    }

    .post-offer-page .section-header span {
        font-weight: 600;
        font-size: 0.8125rem;
        color: var(--text-color);
    }

    /* Success Screen - Compact */
    .post-offer-page .success-screen {
        text-align: center;
        padding: 1.5rem 1rem;
    }

    .post-offer-page .success-icon {
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: #DCFCE7;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 0.75rem;
    }

    .post-offer-page .success-icon i {
        font-size: 1.75rem;
        color: #22C55E;
    }

    .post-offer-page .success-screen h2 {
        font-size: 1.125rem !important;
    }

    .post-offer-page .success-screen p {
        font-size: 0.875rem !important;
    }

    /* Override PrimeReact components for compact design */
    .post-offer-page .p-inputtext {
        padding: 0.5rem 0.75rem;
        font-size: 0.875rem;
    }

    .post-offer-page .p-button {
        padding: 0.5rem 1rem;
        font-size: 0.875rem;
    }

    .post-offer-page .p-button.p-button-sm {
        padding: 0.375rem 0.75rem;
        font-size: 0.8125rem;
    }

    .post-offer-page .p-slider {
        height: 6px;
    }

    .post-offer-page .p-slider .p-slider-handle {
        width: 16px;
        height: 16px;
        margin-top: -5px;
    }

    .post-offer-page .p-tag {
        padding: 0.25rem 0.5rem;
        font-size: 0.75rem;
    }

    .post-offer-page .p-autocomplete-input {
        font-size: 0.875rem;
    }

    .post-offer-page .p-autocomplete-input::placeholder {
        font-size: 0.875rem;
        color: var(--text-color-secondary);
    }

    .post-offer-page .p-dropdown .p-placeholder {
        font-size: 0.875rem;
        color: var(--text-color-secondary);
    }

    .post-offer-page .p-dropdown .p-dropdown-label {
        padding: 0.5rem 0.75rem;
        font-size: 0.875rem;
    }

    .post-offer-page .p-calendar .p-inputtext::placeholder {
        font-size: 0.875rem;
        color: var(--text-color-secondary);
    }

    .post-offer-page .p-chips .p-chips-multiple-container {
        padding: 0.5rem 0.75rem;
        gap: 0.375rem;
        font-size: 0.875rem;
        min-height: 2.25rem;
        align-items: center;
    }

    .post-offer-page .p-chips .p-chips-token {
        font-size: 0.875rem;
        padding: 0.125rem 0.5rem;
        margin: 0;
    }

    .post-offer-page .p-chips .p-chips-input-token {
        padding: 0;
        margin: 0;
    }

    .post-offer-page .p-chips .p-chips-input-token input {
        font-size: 0.875rem;
        padding: 0;
        margin: 0;
    }

    .post-offer-page .p-chips .p-chips-input-token input::placeholder {
        font-size: 0.875rem;
        color: var(--text-color-secondary);
    }

    /* Commission slider custom styles */
    .post-offer-page .commission-slider.p-slider {
        background: #FEE2E2 !important;
        height: 6px !important;
        border-radius: 4px !important;
    }

    .post-offer-page .commission-slider .p-slider-range {
        background: linear-gradient(to right, #22C55E, #F59E0B, #EF4444) !important;
        border-radius: 4px 0 0 4px !important;
    }

    .post-offer-page .commission-slider .p-slider-handle {
        width: 18px !important;
        height: 18px !important;
        margin-top: -6px !important;
        background: white !important;
        border: 2px solid #64748b !important;
        box-shadow: 0 1px 4px rgba(0,0,0,0.2) !important;
    }

    /* Responsive adjustments */
    @media (max-width: 576px) {
        .post-offer-page .form-label {
            font-size: 0.6875rem;
        }
        .post-offer-page .section-header {
            padding: 0.375rem 0.625rem;
        }
        .post-offer-page .section-header span {
            font-size: 0.75rem;
        }
    }
`;

const PostOffer = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const toast = useRef(null);

    // Note: pageStyles rendered inline in JSX to prevent CLS from delayed style injection

    // Edit mode state
    const isEditMode = Boolean(id);
    const [loadingOffer, setLoadingOffer] = useState(false);

    // Step management
    const [activeStep, setActiveStep] = useState(0);

    // Step 1: Bank & Loan Details
    // lenderId / loanTypeId are the canonical master ids that actually get
    // stored; bankName / loanType hold the display labels (kept in sync).
    const [bankName, setBankName] = useState('');
    const [lenderId, setLenderId] = useState(null);
    const [loanType, setLoanType] = useState(null);
    const [loanTypeId, setLoanTypeId] = useState(null);
    const [segment, setSegment] = useState(null);

    // Bank is searched server-side (debounced) — suggestions come from the
    // lenders master per keystroke, never a client-side filter over a big list.
    // bankAc holds the AutoComplete value (string while typing, object once picked).
    const [bankSuggestions, setBankSuggestions] = useState([]);
    const [bankAc, setBankAc] = useState('');

    // Dynamic option lists (loaded from masters — NOT hardcoded).
    const [loanTypeOptionsApi, setLoanTypeOptionsApi] = useState([]);
    const [stateOptions, setStateOptions] = useState([]);
    const [cityOptions, setCityOptions] = useState([]);
    const [loadingCities, setLoadingCities] = useState(false);

    // ── Richer optional details (payout depth / eligibility / operations) ──
    // All nullable on the API — DSAs can fill these progressively.
    const [payoutType, setPayoutType] = useState(null);
    const [payoutOn, setPayoutOn] = useState('');
    const [payoutTimeline, setPayoutTimeline] = useState('');
    const [payoutSlabs, setPayoutSlabs] = useState([]); // [{ amount_upto, pct }]
    const [maxPayoutCap, setMaxPayoutCap] = useState(null);
    const [eligibleEmployment, setEligibleEmployment] = useState(null);
    const [minAge, setMinAge] = useState(null);
    const [maxAge, setMaxAge] = useState(null);
    const [minMonthlyIncome, setMinMonthlyIncome] = useState(null);
    const [tenureMonthsMin, setTenureMonthsMin] = useState(null);
    const [tenureMonthsMax, setTenureMonthsMax] = useState(null);
    const [digitalJourney, setDigitalJourney] = useState(false);
    const [documentsRequired, setDocumentsRequired] = useState([]);
    const [foreclosureNorms, setForeclosureNorms] = useState('');

    // Step 2: Terms & Location
    const [commissionPercentage, setCommissionPercentage] = useState(2.0);
    const [minLoanAmount, setMinLoanAmount] = useState(null);
    const [maxLoanAmount, setMaxLoanAmount] = useState(null);
    const [minCibil, setMinCibil] = useState(null);
    const [processingTime, setProcessingTime] = useState(null);
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [serviceAreas, setServiceAreas] = useState([]);
    const [disbursementDeadline, setDisbursementDeadline] = useState(
        new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
    );
    const [validUntil, setValidUntil] = useState(null);
    const [specializations, setSpecializations] = useState([]);

    // Step 3: Agreements
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [dataAccurate, setDataAccurate] = useState(false);

    // Posting state
    const [posting, setPosting] = useState(false);
    const [postSuccess, setPostSuccess] = useState(false);
    const [newOfferId, setNewOfferId] = useState(null);

    // Validation state
    const [showErrors, setShowErrors] = useState(false);

    // Options
    const segmentOptions = [...DSA_SEGMENTS];

    const processingTimeOptions = [
        { label: '24-48 Hours', value: '24-48 Hours' },
        { label: '3-5 Days', value: '3-5 Days' },
        { label: '1 Week', value: '1 Week' },
        { label: '2 Weeks', value: '2 Weeks' },
        { label: '3-4 Weeks', value: '3-4 Weeks' },
        { label: 'Case to Case Basis', value: 'Case to Case Basis' }
    ];

    // Load dynamic masters once on mount — loan types + states. Each load is
    // INDEPENDENT (no Promise.all) so one failing master never blanks the
    // others. Banks are NOT preloaded — they're searched server-side below.
    useEffect(() => {
        let cancelled = false;
        dsaOfferService.getLoanTypes()
            .then((res) => { if (!cancelled && res?.success) setLoanTypeOptionsApi(res.data || []); })
            .catch((e) => console.error('Failed to load loan types:', e?.message || 'error'));
        axiosInstance.get('/location/states')
            .then((res) => { if (!cancelled && res?.data?.success) setStateOptions(res.data.data || []); })
            .catch((e) => console.error('Failed to load states:', e?.message || 'error'));
        return () => { cancelled = true; };
    }, []);

    // Bank search — hits the lenders master on the BACKEND, debounced by the
    // AutoComplete's native `delay` prop (no client-side filtering of a big list).
    const searchBanks = useCallback(async (event) => {
        try {
            const res = await dsaOfferService.getBankSuggestions(event?.query || '');
            setBankSuggestions(res?.success ? (res.data || []) : []);
        } catch (e) {
            console.error('Bank search failed:', e?.message || 'error');
            setBankSuggestions([]);
        }
    }, []);

    // Cascading cities for the chosen state (canonical location master).
    const loadCities = useCallback(async (selectedState) => {
        if (!selectedState) { setCityOptions([]); return; }
        setLoadingCities(true);
        try {
            const res = await axiosInstance.get(`/location/cities/by-state/${encodeURIComponent(selectedState)}`);
            setCityOptions(res?.data?.success ? (res.data.data || []) : []);
        } catch {
            setCityOptions([]);
        } finally {
            setLoadingCities(false);
        }
    }, []);

    // Fetch offer data for edit mode
    useEffect(() => {
        const fetchOfferForEdit = async () => {
            if (!isEditMode) return;

            setLoadingOffer(true);
            try {
                const response = await dsaOfferService.getOfferDetails(id);
                if (response.success && response.data) {
                    const offer = response.data;
                    setLenderId(offer.lender_id || null);
                    setLoanTypeId(offer.loan_type_id || null);
                    setBankName(offer.bank_name || '');
                    setBankAc(offer.lender_id ? { id: offer.lender_id, bank_name: offer.bank_name } : (offer.bank_name || ''));
                    setLoanType(offer.loan_type || null);
                    setSegment(offer.segment || null);
                    setCommissionPercentage(offer.commission_percentage || 2.0);
                    setMinLoanAmount(offer.min_loan_amount || 100000);
                    setMaxLoanAmount(offer.max_loan_amount || 5000000);
                    setMinCibil(offer.min_cibil || 650);
                    setProcessingTime(offer.processing_time || '');
                    setState(offer.state || '');
                    if (offer.state) loadCities(offer.state);
                    setCity(offer.city || '');
                    // Richer optional details — populate if present so editing
                    // preserves what the DSA filled earlier.
                    setPayoutType(offer.payout_type || null);
                    setPayoutOn(offer.payout_on || '');
                    setPayoutTimeline(offer.payout_timeline || '');
                    setPayoutSlabs(Array.isArray(offer.payout_slabs) ? offer.payout_slabs : []);
                    setMaxPayoutCap(offer.max_payout_cap || null);
                    setEligibleEmployment(offer.eligible_employment || null);
                    setMinAge(offer.min_age || null);
                    setMaxAge(offer.max_age || null);
                    setMinMonthlyIncome(offer.min_monthly_income || null);
                    setTenureMonthsMin(offer.tenure_months_min || null);
                    setTenureMonthsMax(offer.tenure_months_max || null);
                    setDigitalJourney(!!offer.digital_journey);
                    setDocumentsRequired(Array.isArray(offer.documents_required) ? offer.documents_required : []);
                    setForeclosureNorms(offer.foreclosure_norms || '');
                    setServiceAreas(offer.service_areas || []);
                    setSpecializations(offer.specializations || []);

                    if (offer.disbursement_deadline) {
                        setDisbursementDeadline(new Date(offer.disbursement_deadline));
                    }
                    if (offer.valid_until) {
                        setValidUntil(new Date(offer.valid_until));
                    }

                    setTermsAccepted(true);
                    setDataAccurate(true);
                } else {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to load offer for editing'
                    });
                    navigate('/app/dsa-offers/my-offers');
                }
            } catch (error) {
                console.error('Error fetching offer for edit:', error);
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to load offer'
                });
                navigate('/app/dsa-offers/my-offers');
            } finally {
                setLoadingOffer(false);
            }
        };

        fetchOfferForEdit();
    }, [id, isEditMode, navigate]);

    // Quick deadline options
    const deadlineOptions = [
        { label: '15 Days', days: 15 },
        { label: '30 Days', days: 30 },
        { label: '45 Days', days: 45 },
        { label: '60 Days', days: 60 }
    ];

    const setQuickDeadline = (days) => {
        setDisbursementDeadline(new Date(Date.now() + days * 24 * 60 * 60 * 1000));
    };

    const getDaysUntilDeadline = () => {
        const now = new Date();
        const deadline = new Date(disbursementDeadline);
        const diffTime = deadline - now;
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    // Validation - returns object with field keys for inline errors
    const getFieldErrors = (step) => {
        const errors = {};
        if (step === 0) {
            if (!lenderId) errors.bankName = 'Select a bank / lender';
            if (!loanTypeId) errors.loanType = 'Select a loan type';
            if (!commissionPercentage || commissionPercentage <= 0) errors.commission = 'Set a commission rate';
            if (!minLoanAmount || minLoanAmount <= 0) errors.minLoanAmount = 'Enter minimum loan amount';
            if (!maxLoanAmount || maxLoanAmount <= 0) errors.maxLoanAmount = 'Enter maximum loan amount';
            if (minLoanAmount && maxLoanAmount && maxLoanAmount <= minLoanAmount) errors.maxLoanAmount = 'Must be greater than minimum amount';
            if (!city) errors.city = 'City is required';
            if (!state) errors.state = 'Select a state';
            if (!disbursementDeadline) errors.deadline = 'Select a disbursement date';
        }
        if (step === 1) {
            if (!termsAccepted) errors.terms = 'Accept Terms & Conditions to proceed';
            if (!dataAccurate) errors.accuracy = 'Confirm data accuracy to proceed';
        }
        return errors;
    };

    const scrollToFirstError = () => {
        setTimeout(() => {
            const firstError = document.querySelector('.post-offer-page .field-error');
            if (firstError) {
                firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 100);
    };

    const hasError = (field) => showErrors && getFieldErrors(activeStep)[field];

    const nextStep = () => {
        if (activeStep < 1) {
            const errors = getFieldErrors(activeStep);
            if (Object.keys(errors).length > 0) {
                setShowErrors(true);
                scrollToFirstError();
                return;
            }
            setShowErrors(false);
            setActiveStep(activeStep + 1);
        }
    };

    const prevStep = () => {
        if (activeStep > 0) {
            setShowErrors(false);
            setActiveStep(activeStep - 1);
        }
    };

    // Handle post/update
    const handlePost = async () => {
        const errors = getFieldErrors(activeStep);
        if (Object.keys(errors).length > 0) {
            setShowErrors(true);
            scrollToFirstError();
            return;
        }

        setPosting(true);
        try {
            const payload = {
                lender_id: lenderId,
                loan_type_id: loanTypeId,
                bank_name: bankName || undefined,
                loan_type: loanType || undefined,
                segment: segment || undefined,
                commission_percentage: commissionPercentage,
                // Payout depth (optional, additive richer detail).
                payout_type: payoutType || undefined,
                payout_on: payoutOn || undefined,
                payout_timeline: payoutTimeline || undefined,
                payout_slabs: payoutSlabs.length > 0 ? payoutSlabs : undefined,
                max_payout_cap: maxPayoutCap || undefined,
                min_loan_amount: minLoanAmount,
                max_loan_amount: maxLoanAmount,
                min_cibil: minCibil || undefined,
                // Eligibility.
                eligible_employment: eligibleEmployment || undefined,
                min_age: minAge || undefined,
                max_age: maxAge || undefined,
                min_monthly_income: minMonthlyIncome || undefined,
                tenure_months_min: tenureMonthsMin || undefined,
                tenure_months_max: tenureMonthsMax || undefined,
                // Operations.
                processing_time: processingTime || undefined,
                digital_journey: digitalJourney,
                documents_required: documentsRequired.length > 0 ? documentsRequired : undefined,
                foreclosure_norms: foreclosureNorms || undefined,
                city: city,
                state: state,
                service_areas: serviceAreas.length > 0 ? serviceAreas : undefined,
                disbursement_deadline: disbursementDeadline.toISOString().split('T')[0],
                valid_until: validUntil ? validUntil.toISOString().split('T')[0] : undefined,
                specializations: specializations.length > 0 ? specializations : undefined
            };

            const response = isEditMode
                ? await dsaOfferService.updateOffer(id, payload)
                : await dsaOfferService.createOffer(payload);

            if (response.success) {
                setPostSuccess(true);
                setNewOfferId(isEditMode ? id : (response.data?.offer?.id || response.data?.id));
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success!',
                    detail: isEditMode ? 'Your offer has been updated' : 'Your DSA offer has been published'
                });
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: response.message || `Failed to ${isEditMode ? 'update' : 'create'} offer`
                });
            }
        } catch (error) {
            console.error(`Failed to ${isEditMode ? 'update' : 'post'} offer:`, error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || `Failed to ${isEditMode ? 'update' : 'create'} offer`
            });
        } finally {
            setPosting(false);
        }
    };

    // Wizard Steps Component
    const WizardSteps = () => (
        <div className="wizard-steps">
            <div className={`wizard-step ${activeStep > 0 ? 'completed' : activeStep === 0 ? 'active' : ''}`}>
                <div className="wizard-step-number">
                    {activeStep > 0 ? <i className="pi pi-check text-xs" /> : '1'}
                </div>
                <span className="wizard-step-label">Offer Details</span>
            </div>
            <div className={`wizard-step-connector ${activeStep > 0 ? 'completed' : ''}`} />
            <div className={`wizard-step ${activeStep === 1 ? 'active' : ''}`}>
                <div className="wizard-step-number">2</div>
                <span className="wizard-step-label">Review</span>
            </div>
        </div>
    );

    // Summary Sidebar Component
    const SummarySidebar = () => (
        <div className="summary-sidebar surface-card border-round shadow-1 p-3">
            <h4 className="m-0 mb-2 font-semibold text-sm">Offer Summary</h4>

            {bankName ? (
                <>
                    <div className="border-bottom-1 border-200 pb-2 mb-2">
                        <div className="text-xs text-500 mb-1">Bank / Lender</div>
                        <div className="font-semibold text-sm">{bankName}</div>
                        {loanType && (
                            <div className="text-xs text-500 mt-1">
                                {loanType}
                                {segment && ` - ${segment}`}
                            </div>
                        )}
                    </div>

                    <div className="grid text-xs mb-2" style={{ rowGap: '0.5rem' }}>
                        <div className="col-6 text-500">Commission</div>
                        <div className="col-6 text-right font-medium text-green-600">{formatCommission(commissionPercentage)}</div>

                        <div className="col-6 text-500">Loan Range</div>
                        <div className="col-6 text-right font-medium">{formatLoanRange(minLoanAmount, maxLoanAmount)}</div>

                        {minCibil > 0 && (
                            <>
                                <div className="col-6 text-500">Min CIBIL</div>
                                <div className="col-6 text-right font-medium">{minCibil}+</div>
                            </>
                        )}

                        {city && (
                            <>
                                <div className="col-6 text-500">Location</div>
                                <div className="col-6 text-right font-medium">{city}{state ? `, ${state}` : ''}</div>
                            </>
                        )}

                        {processingTime && (
                            <>
                                <div className="col-6 text-500">Processing</div>
                                <div className="col-6 text-right font-medium">{processingTime}</div>
                            </>
                        )}

                        <div className="col-6 text-500">Deadline</div>
                        <div className="col-6 text-right font-medium">
                            {disbursementDeadline?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        </div>
                    </div>
                </>
            ) : (
                <div className="text-xs text-500">Fill in details to see summary</div>
            )}

            <div className="bg-blue-50 border-round p-2 text-xs">
                <div className="flex gap-2">
                    <i className="pi pi-info-circle text-blue-500 flex-shrink-0 text-xs" />
                    <div>
                        <div className="text-blue-700 font-medium mb-1">Visibility</div>
                        <div className="text-blue-600 line-height-3">Your offer will be visible to Lead Owners who can invite you to their leads.</div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Loading skeleton
    const renderLoadingSkeleton = () => (
        <div className="post-offer-page">
            <style>{pageStyles}</style>
            <div className="mb-3">
                <Skeleton width="180px" height="1.25rem" className="mb-2" />
                <Skeleton width="280px" height="0.875rem" />
            </div>
            <div className="surface-card border-round shadow-1 p-3 mb-3">
                <Skeleton width="100%" height="2.5rem" className="mb-3" />
            </div>
            <div className="grid">
                <div className="col-12 lg:col-8">
                    <div className="surface-card border-round shadow-1 p-3">
                        <Skeleton width="160px" height="1rem" className="mb-3" />
                        {[1, 2, 3].map(i => (
                            <div key={i} className="mb-3">
                                <Skeleton width="100px" height="0.75rem" className="mb-2" />
                                <Skeleton width="100%" height="2.5rem" />
                            </div>
                        ))}
                    </div>
                </div>
                <div className="col-12 lg:col-4">
                    <div className="surface-card border-round shadow-1 p-3">
                        <Skeleton width="120px" height="0.875rem" className="mb-3" />
                        {[1, 2, 3].map(i => (
                            <div key={i} className="flex justify-content-between mb-2">
                                <Skeleton width="80px" height="0.75rem" />
                                <Skeleton width="60px" height="0.75rem" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    // Loading state for edit mode
    if (loadingOffer) {
        return renderLoadingSkeleton();
    }

    // Success screen
    if (postSuccess) {
        return (
            <div className="post-offer-page">
                <style>{pageStyles}</style>
                <Toast ref={toast} />
                <div className="success-screen">
                    <div className="success-icon">
                        <i className="pi pi-check" />
                    </div>
                    <h2 className="text-900 font-bold mb-2 mt-0">
                        {isEditMode ? 'Offer Updated!' : 'Offer Published!'}
                    </h2>
                    <p className="text-600 mb-4" style={{ maxWidth: '400px', margin: '0 auto' }}>
                        {isEditMode
                            ? 'Your changes have been saved. Lead Owners will now see the updated offer.'
                            : 'Your DSA commission offer is now live. Lead Owners can now see your offer and invite you to their leads.'}
                    </p>
                    <div className="flex flex-wrap justify-content-center gap-3">
                        <Button
                            label="View Offer"
                            icon="pi pi-eye"
                            onClick={() => navigate(`/app/dsa-offers/${newOfferId}`)}
                        />
                        {!isEditMode && (
                            <Button
                                label="Post Another"
                                icon="pi pi-plus"
                                className="p-button-outlined"
                                onClick={() => {
                                    setPostSuccess(false);
                                    setBankName('');
                                    setLoanType(null);
                                    setSegment(null);
                                    setCommissionPercentage(2.0);
                                    setActiveStep(0);
                                    setTermsAccepted(false);
                                    setDataAccurate(false);
                                }}
                            />
                        )}
                        <Button
                            label="My Offers"
                            icon="pi pi-list"
                            className="p-button-text"
                            onClick={() => navigate('/app/dsa-offers/my-offers')}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="post-offer-page">
            <style>{pageStyles}</style>
            <NavigationTabMenu tabs={MY_PAYOUTS_TABS} />
            <Toast ref={toast} />

            {/* Page Header - Compact */}
            <div className="surface-card border-round shadow-1 p-2 mb-2">
                <div className="flex align-items-center justify-content-between">
                    <h2 className="text-900 font-bold m-0" style={{ fontSize: '1rem' }}>
                        {isEditMode ? 'Edit Payout' : 'Post Payout'}
                    </h2>
                    <div className="flex align-items-center gap-1">
                        <Button
                            label="My Payouts"
                            icon="pi pi-list"
                            className="p-button-outlined p-button-sm"
                            onClick={() => navigate('/app/dsa-offers/my-offers')}
                            style={{ fontSize: '0.7rem', padding: '0.35rem 0.6rem' }}
                        />
                    </div>
                </div>
            </div>

            {/* Wizard Steps */}
            <WizardSteps />

            <div className="grid">
                {/* Main Content */}
                <div className="col-12 lg:col-8">
                    {/* Step 1: Offer Details */}
                    {activeStep === 0 && (
                        <>
                            {/* Bank & Loan Section */}
                            <div className="surface-card border-round shadow-1 p-3 mb-3">
                                <div className="section-header">
                                    <i className="pi pi-building" />
                                    <span>Bank & Loan Details</span>
                                </div>

                                {/* Bank Name */}
                                <div className={`form-group ${hasError('bankName') ? 'field-error' : ''}`}>
                                    <label className="form-label required">Bank / Lender</label>
                                    <AutoComplete
                                        value={bankAc}
                                        suggestions={bankSuggestions}
                                        completeMethod={searchBanks}
                                        field="bank_name"
                                        delay={350}
                                        dropdown
                                        forceSelection
                                        onChange={(e) => {
                                            setBankAc(e.value);
                                            // Typing (string) invalidates the previous pick until
                                            // the user selects a real lender from the list.
                                            if (typeof e.value === 'string') {
                                                setLenderId(null);
                                                setBankName('');
                                            }
                                        }}
                                        onSelect={(e) => {
                                            setLenderId(e.value.id);
                                            setBankName(e.value.bank_name);
                                            setBankAc(e.value);
                                        }}
                                        placeholder="Search bank / lender..."
                                        className="w-full"
                                        inputClassName="w-full"
                                        inputId="bank_name"
                                        name="bank_name"
                                        data-testid="bank_name"
                                    />
                                    {hasError('bankName') ? <div className="form-error">{hasError('bankName')}</div> : <div className="form-help">Type to search — pick a lender from the list</div>}
                                </div>

                                {/* Loan Type & Segment */}
                                <div className="form-group">
                                    <div className="grid">
                                        <div className={`col-12 md:col-6 ${hasError('loanType') ? 'field-error' : ''}`}>
                                            <label className="form-label required">Loan Type</label>
                                            <Dropdown
                                                value={loanTypeId}
                                                options={loanTypeOptionsApi}
                                                onChange={(e) => {
                                                    setLoanTypeId(e.value);
                                                    const opt = loanTypeOptionsApi.find((o) => o.id === e.value);
                                                    setLoanType(opt ? opt.label : '');
                                                }}
                                                optionLabel="label"
                                                optionValue="id"
                                                filter
                                                placeholder="Select loan type"
                                                className="w-full"
                                                inputId="loan_type"
                                                name="loan_type"
                                                data-testid="loan_type"
                                            />
                                            {hasError('loanType') && <div className="form-error">{hasError('loanType')}</div>}
                                        </div>
                                        <div className="col-12 md:col-6">
                                            <label className="form-label">Segment (Optional)</label>
                                            <Dropdown
                                                value={segment}
                                                options={segmentOptions}
                                                onChange={(e) => setSegment(e.value)}
                                                optionLabel="label"
                                                optionValue="value"
                                                placeholder="Select segment"
                                                className="w-full"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Specializations */}
                                <div className="form-group mb-0">
                                    <label className="form-label">Specializations (Optional)</label>
                                    <Chips
                                        value={specializations}
                                        onChange={(e) => setSpecializations(e.value)}
                                        placeholder="Type and press Enter"
                                        className="w-full"
                                    />
                                    <div className="form-help">e.g., NRI Loans, Self Employed, Salaried</div>
                                </div>
                            </div>

                            {/* Commission Section */}
                            <div className="surface-card border-round shadow-1 p-3 mb-3">
                                <div className="section-header">
                                    <i className="pi pi-percentage" />
                                    <span>Commission Rate</span>
                                </div>

                                <div className="form-group mb-0">
                                    <div className="surface-100 border-round p-2">
                                        <div className="flex justify-content-between align-items-center mb-2">
                                            <span className="text-xs text-500">Set the commission percentage you offer</span>
                                            <span
                                                className="text-xl font-bold"
                                                style={{ color: commissionPercentage <= 2 ? '#22C55E' : commissionPercentage <= 5 ? '#F59E0B' : '#EF4444' }}
                                            >
                                                {formatCommission(commissionPercentage)}
                                            </span>
                                        </div>
                                        <Slider
                                            value={commissionPercentage}
                                            onChange={(e) => setCommissionPercentage(e.value)}
                                            min={0.5}
                                            max={10}
                                            step={0.001}
                                            className="w-full mb-1 commission-slider"
                                            data-testid="commission_rate"
                                        />
                                        <div className="flex justify-content-between text-xs text-500">
                                            <span>0.5%</span>
                                            <span>10%</span>
                                        </div>
                                        {/* Manual Entry */}
                                        <div className="flex align-items-center justify-content-center gap-2 mt-2 pt-2 border-top-1 border-200">
                                            <span className="text-xs text-500">Or enter:</span>
                                            <InputNumber
                                                value={commissionPercentage}
                                                onValueChange={(e) => setCommissionPercentage(Math.min(10, Math.max(0.5, e.value || 0.5)))}
                                                mode="decimal"
                                                minFractionDigits={1}
                                                maxFractionDigits={3}
                                                min={0.5}
                                                max={10}
                                                suffix="%"
                                                inputClassName="text-center font-bold"
                                                inputId="interest_rate"
                                                name="interest_rate"
                                                data-testid="interest_rate"
                                                inputStyle={{
                                                    color: commissionPercentage <= 2 ? '#22C55E' : commissionPercentage <= 5 ? '#F59E0B' : '#EF4444',
                                                    width: '6rem',
                                                    fontSize: '0.9375rem'
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Loan Requirements Section */}
                            <div className="surface-card border-round shadow-1 p-3 mb-3">
                                <div className="section-header">
                                    <i className="pi pi-money-bill" />
                                    <span>Loan Requirements</span>
                                </div>

                                {/* Loan Amount Range */}
                                <div className="form-group">
                                    <label className="form-label required">Loan Amount Range</label>
                                    <div className="grid">
                                        <div className={`col-6 ${hasError('minLoanAmount') ? 'field-error' : ''}`}>
                                            <label className="form-label" style={{ fontWeight: 500 }}>Minimum</label>
                                            <InputNumber
                                                value={minLoanAmount}
                                                onValueChange={(e) => setMinLoanAmount(e.value)}
                                                mode="currency"
                                                currency="INR"
                                                locale="en-IN"
                                                placeholder="e.g., 1,00,000"
                                                className="w-full"
                                            />
                                            {hasError('minLoanAmount') && <div className="form-error">{hasError('minLoanAmount')}</div>}
                                        </div>
                                        <div className={`col-6 ${hasError('maxLoanAmount') ? 'field-error' : ''}`}>
                                            <label className="form-label" style={{ fontWeight: 500 }}>Maximum</label>
                                            <InputNumber
                                                value={maxLoanAmount}
                                                onValueChange={(e) => setMaxLoanAmount(e.value)}
                                                mode="currency"
                                                currency="INR"
                                                locale="en-IN"
                                                placeholder="e.g., 50,00,000"
                                                className="w-full"
                                            />
                                            {hasError('maxLoanAmount') && <div className="form-error">{hasError('maxLoanAmount')}</div>}
                                        </div>
                                    </div>
                                    {!hasError('minLoanAmount') && !hasError('maxLoanAmount') && <div className="form-help">Enter numbers only, currency format is applied automatically</div>}
                                </div>

                                {/* Min CIBIL & Processing Time */}
                                <div className="form-group mb-0">
                                    <div className="grid">
                                        <div className="col-12 md:col-6">
                                            <label className="form-label">Min CIBIL Score (Optional)</label>
                                            <InputNumber
                                                value={minCibil}
                                                onValueChange={(e) => setMinCibil(e.value)}
                                                min={300}
                                                max={900}
                                                placeholder="e.g., 650"
                                                className="w-full"
                                            />
                                            <div className="form-help">Minimum credit score required for the loan</div>
                                        </div>
                                        <div className="col-12 md:col-6">
                                            <label className="form-label">Processing Time (Optional)</label>
                                            <Dropdown
                                                value={processingTime}
                                                options={processingTimeOptions}
                                                onChange={(e) => setProcessingTime(e.value)}
                                                placeholder="Select processing time"
                                                className="w-full"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Optional richer details — Payout depth / Eligibility
                                / Operations. Collapsed by default; DSAs can fill
                                these progressively to make their offer card richer.
                                All fields are nullable on the API, so an offer is
                                still valid even when these stay empty. */}
                            <div className="surface-card border-round shadow-1 mb-3">
                                <Accordion multiple>
                                    {/* ── Payout depth ── */}
                                    <AccordionTab header={
                                        <span className="flex align-items-center gap-2 text-sm font-medium">
                                            <i className="pi pi-percentage text-primary" />
                                            Payout Structure (optional)
                                        </span>
                                    }>
                                        <div className="grid">
                                            <div className="col-12 md:col-6">
                                                <label className="form-label">Payout Type</label>
                                                <Dropdown
                                                    value={payoutType}
                                                    options={[
                                                        { label: 'Instant (T+0)', value: 'instant' },
                                                        { label: 'Monthly', value: 'monthly' },
                                                        { label: 'Advance (split)', value: 'advance' },
                                                    ]}
                                                    onChange={(e) => setPayoutType(e.value)}
                                                    optionLabel="label"
                                                    optionValue="value"
                                                    showClear
                                                    placeholder="Select payout type"
                                                    className="w-full"
                                                />
                                            </div>
                                            <div className="col-12 md:col-6">
                                                <label className="form-label">Payout On</label>
                                                <InputText
                                                    value={payoutOn}
                                                    onChange={(e) => setPayoutOn(e.target.value)}
                                                    placeholder="e.g., Disbursed amount"
                                                    className="w-full"
                                                />
                                            </div>
                                            <div className="col-12">
                                                <label className="form-label">Payout Timeline</label>
                                                <InputText
                                                    value={payoutTimeline}
                                                    onChange={(e) => setPayoutTimeline(e.target.value)}
                                                    placeholder="e.g., T+0 days (instant on disbursal)"
                                                    className="w-full"
                                                />
                                            </div>
                                            <div className="col-12">
                                                <label className="form-label">Commission Slabs (tiered %)</label>
                                                <div className="text-xs text-500 mb-2">
                                                    Up to a loan amount, the DSA earns this %. Leave "Amount up to" empty on the last row for "above the previous tier".
                                                </div>
                                                {payoutSlabs.map((slab, idx) => (
                                                    <div key={idx} className="grid mb-1 align-items-end">
                                                        <div className="col-7 md:col-6">
                                                            <InputNumber
                                                                value={slab.amount_upto}
                                                                onValueChange={(e) => {
                                                                    const next = [...payoutSlabs];
                                                                    next[idx] = { ...next[idx], amount_upto: e.value };
                                                                    setPayoutSlabs(next);
                                                                }}
                                                                mode="currency" currency="INR" locale="en-IN"
                                                                placeholder="Amount up to (₹)"
                                                                className="w-full"
                                                            />
                                                        </div>
                                                        <div className="col-3 md:col-4">
                                                            <InputNumber
                                                                value={slab.pct}
                                                                onValueChange={(e) => {
                                                                    const next = [...payoutSlabs];
                                                                    next[idx] = { ...next[idx], pct: e.value };
                                                                    setPayoutSlabs(next);
                                                                }}
                                                                mode="decimal" minFractionDigits={2} maxFractionDigits={3}
                                                                suffix="%" min={0} max={100}
                                                                placeholder="%"
                                                                className="w-full"
                                                            />
                                                        </div>
                                                        <div className="col-2">
                                                            <Button
                                                                icon="pi pi-times"
                                                                severity="danger"
                                                                outlined
                                                                size="small"
                                                                onClick={() => setPayoutSlabs(payoutSlabs.filter((_, i) => i !== idx))}
                                                                aria-label="Remove slab"
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                                <Button
                                                    label="Add slab"
                                                    icon="pi pi-plus"
                                                    size="small"
                                                    outlined
                                                    onClick={() => setPayoutSlabs([...payoutSlabs, { amount_upto: null, pct: null }])}
                                                />
                                            </div>
                                            <div className="col-12 md:col-6">
                                                <label className="form-label">Max Payout Cap (₹)</label>
                                                <InputNumber
                                                    value={maxPayoutCap}
                                                    onValueChange={(e) => setMaxPayoutCap(e.value)}
                                                    mode="currency" currency="INR" locale="en-IN"
                                                    placeholder="e.g., 3,00,000"
                                                    className="w-full"
                                                />
                                            </div>
                                        </div>
                                    </AccordionTab>

                                    {/* ── Borrower eligibility ── */}
                                    <AccordionTab header={
                                        <span className="flex align-items-center gap-2 text-sm font-medium">
                                            <i className="pi pi-id-card text-primary" />
                                            Borrower Eligibility (optional)
                                        </span>
                                    }>
                                        <div className="grid">
                                            <div className="col-12 md:col-4">
                                                <label className="form-label">Eligible Employment</label>
                                                <Dropdown
                                                    value={eligibleEmployment}
                                                    options={[
                                                        { label: 'Salaried', value: 'salaried' },
                                                        { label: 'Self-Employed', value: 'self_employed' },
                                                        { label: 'Both', value: 'both' },
                                                    ]}
                                                    onChange={(e) => setEligibleEmployment(e.value)}
                                                    optionLabel="label" optionValue="value"
                                                    showClear placeholder="Any" className="w-full"
                                                />
                                            </div>
                                            <div className="col-6 md:col-4">
                                                <label className="form-label">Min Age</label>
                                                <InputNumber value={minAge} onValueChange={(e) => setMinAge(e.value)} min={18} max={99} placeholder="23" className="w-full" />
                                            </div>
                                            <div className="col-6 md:col-4">
                                                <label className="form-label">Max Age</label>
                                                <InputNumber value={maxAge} onValueChange={(e) => setMaxAge(e.value)} min={18} max={99} placeholder="60" className="w-full" />
                                            </div>
                                            <div className="col-12 md:col-4">
                                                <label className="form-label">Min Monthly Income (₹)</label>
                                                <InputNumber value={minMonthlyIncome} onValueChange={(e) => setMinMonthlyIncome(e.value)} mode="currency" currency="INR" locale="en-IN" placeholder="25,000" className="w-full" />
                                            </div>
                                            <div className="col-6 md:col-4">
                                                <label className="form-label">Tenure Min (months)</label>
                                                <InputNumber value={tenureMonthsMin} onValueChange={(e) => setTenureMonthsMin(e.value)} min={1} max={600} placeholder="12" className="w-full" />
                                            </div>
                                            <div className="col-6 md:col-4">
                                                <label className="form-label">Tenure Max (months)</label>
                                                <InputNumber value={tenureMonthsMax} onValueChange={(e) => setTenureMonthsMax(e.value)} min={1} max={600} placeholder="60" className="w-full" />
                                            </div>
                                        </div>
                                    </AccordionTab>

                                    {/* ── Operations & trust ── */}
                                    <AccordionTab header={
                                        <span className="flex align-items-center gap-2 text-sm font-medium">
                                            <i className="pi pi-cog text-primary" />
                                            Operations & Trust (optional)
                                        </span>
                                    }>
                                        <div className="grid">
                                            <div className="col-12">
                                                <div className="flex align-items-center gap-2">
                                                    <Checkbox inputId="digital_journey" checked={!!digitalJourney} onChange={(e) => setDigitalJourney(e.checked)} />
                                                    <label htmlFor="digital_journey" className="text-sm">Fully digital application journey</label>
                                                </div>
                                            </div>
                                            <div className="col-12">
                                                <label className="form-label">Documents Required</label>
                                                <Chips value={documentsRequired} onChange={(e) => setDocumentsRequired(e.value)} placeholder="Type and press Enter (e.g., PAN, Aadhaar, Salary Slips)" className="w-full" />
                                            </div>
                                            <div className="col-12">
                                                <label className="form-label">Foreclosure Norms</label>
                                                <InputTextarea value={foreclosureNorms} onChange={(e) => setForeclosureNorms(e.target.value)} rows={2} autoResize placeholder="e.g., No charges after 12 EMIs" className="w-full" />
                                            </div>
                                        </div>
                                    </AccordionTab>
                                </Accordion>
                            </div>

                            {/* Location Section */}
                            <div className="surface-card border-round shadow-1 p-3 mb-3">
                                <div className="section-header">
                                    <i className="pi pi-map-marker" />
                                    <span>Service Location</span>
                                </div>

                                {/* City & State */}
                                <div className="form-group">
                                    <label className="form-label required">Location</label>
                                    <div className="grid">
                                        {/* State first — City is cascading (depends on state) */}
                                        <div className={`col-6 ${hasError('state') ? 'field-error' : ''}`}>
                                            <label className="form-label required" style={{ fontWeight: 500 }}>State</label>
                                            <Dropdown
                                                value={state}
                                                options={stateOptions}
                                                onChange={(e) => {
                                                    setState(e.value);
                                                    setCity('');
                                                    loadCities(e.value);
                                                }}
                                                optionLabel="label"
                                                optionValue="value"
                                                placeholder="Select state"
                                                filter
                                                showClear
                                                className="w-full"
                                                inputId="state"
                                                name="state"
                                                data-testid="state"
                                            />
                                            {hasError('state') && <div className="form-error">{hasError('state')}</div>}
                                        </div>
                                        <div className={`col-6 ${hasError('city') ? 'field-error' : ''}`}>
                                            <label className="form-label required" style={{ fontWeight: 500 }}>City</label>
                                            <Dropdown
                                                value={city}
                                                options={cityOptions}
                                                onChange={(e) => setCity(e.value)}
                                                optionLabel="label"
                                                optionValue="value"
                                                filter
                                                showClear
                                                disabled={!state || loadingCities}
                                                placeholder={loadingCities ? 'Loading…' : (state ? 'Select city' : 'Select state first')}
                                                className="w-full"
                                                inputId="city"
                                                name="city"
                                                data-testid="city"
                                                emptyMessage="No cities found"
                                            />
                                            {hasError('city') && <div className="form-error">{hasError('city')}</div>}
                                        </div>
                                    </div>
                                </div>

                                {/* Service Areas */}
                                <div className="form-group mb-0">
                                    <label className="form-label">Service Areas (Optional)</label>
                                    <Chips
                                        value={serviceAreas}
                                        onChange={(e) => setServiceAreas(e.value)}
                                        placeholder="Type and press Enter"
                                        className="w-full"
                                    />
                                    <div className="form-help">e.g., Pune, Thane, Navi Mumbai</div>
                                </div>
                            </div>

                            {/* Deadline Section */}
                            <div className="surface-card border-round shadow-1 p-3 mb-3">
                                <div className="section-header">
                                    <i className="pi pi-calendar" />
                                    <span>Disbursement Deadline</span>
                                </div>

                                <div className={`form-group mb-0 ${hasError('deadline') ? 'field-error' : ''}`}>
                                    <label className="form-label required">Disbursement Date</label>
                                    <div className="grid">
                                        <div className="col-12 md:col-6">
                                            <Calendar
                                                value={disbursementDeadline}
                                                onChange={(e) => setDisbursementDeadline(e.value)}
                                                minDate={new Date()}
                                                showIcon
                                                className="w-full"
                                                dateFormat="M dd, yy"
                                            />
                                        </div>
                                        <div className="col-12 md:col-6">
                                            <div className="quick-chips h-full align-items-center">
                                                {deadlineOptions.map(opt => (
                                                    <button
                                                        key={opt.days}
                                                        className={`quick-chip ${getDaysUntilDeadline() === opt.days ? 'active' : ''}`}
                                                        onClick={() => setQuickDeadline(opt.days)}
                                                    >
                                                        {opt.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Step 2: Review */}
                    {activeStep === 1 && (
                        <>
                            <div className="surface-card border-round shadow-1 p-3 mb-3">
                                <h3 className="m-0 font-semibold mb-3">Review Your Offer</h3>

                                {/* Offer Details */}
                                <div className="mb-3">
                                    <h4 className="text-xs text-500 font-medium mb-2 mt-0">BANK & LOAN</h4>
                                    <div className="surface-100 border-round p-2">
                                        <div className="grid" style={{ rowGap: '0.75rem' }}>
                                            <div className="col-6 md:col-4">
                                                <div className="text-xs text-500 mb-1">Bank / Lender</div>
                                                <div className="font-bold text-900 text-sm">{bankName}</div>
                                            </div>
                                            <div className="col-6 md:col-4">
                                                <div className="text-xs text-500 mb-1">Loan Type</div>
                                                <Tag
                                                    value={loanType}
                                                    severity="info"
                                                    style={{ fontSize: '0.6875rem', padding: '0.2rem 0.5rem' }}
                                                />
                                            </div>
                                            {segment && (
                                                <div className="col-6 md:col-4">
                                                    <div className="text-xs text-500 mb-1">Segment</div>
                                                    <Tag value={segment} severity="secondary" style={{ fontSize: '0.6875rem', padding: '0.2rem 0.5rem' }} />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Terms */}
                                <div className="mb-3">
                                    <h4 className="text-xs text-500 font-medium mb-2 mt-0">TERMS & COVERAGE</h4>
                                    <div className="surface-100 border-round p-2">
                                        <div className="grid" style={{ rowGap: '0.75rem' }}>
                                            <div className="col-6 md:col-3">
                                                <div className="text-xs text-500 mb-1">Commission</div>
                                                <div className="font-bold text-green-600 text-base">{formatCommission(commissionPercentage)}</div>
                                            </div>
                                            <div className="col-6 md:col-3">
                                                <div className="text-xs text-500 mb-1">Loan Range</div>
                                                <div className="font-semibold text-700 text-sm">{formatLoanRange(minLoanAmount, maxLoanAmount)}</div>
                                            </div>
                                            {minCibil > 0 && (
                                                <div className="col-6 md:col-3">
                                                    <div className="text-xs text-500 mb-1">Min CIBIL</div>
                                                    <div className="font-semibold text-700 text-sm">{minCibil}+</div>
                                                </div>
                                            )}
                                            {processingTime && (
                                                <div className="col-6 md:col-3">
                                                    <div className="text-xs text-500 mb-1">Processing Time</div>
                                                    <div className="font-semibold text-700 text-sm">{processingTime}</div>
                                                </div>
                                            )}
                                            <div className="col-6 md:col-3">
                                                <div className="text-xs text-500 mb-1">Location</div>
                                                <div className="font-semibold text-700 text-sm">{city}, {state}</div>
                                            </div>
                                            <div className="col-6 md:col-3">
                                                <div className="text-xs text-500 mb-1">Deadline</div>
                                                <div className="font-semibold text-700 text-sm">
                                                    {disbursementDeadline?.toLocaleDateString('en-IN', {
                                                        day: 'numeric',
                                                        month: 'short',
                                                        year: 'numeric'
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Service Areas & Specializations */}
                                {(serviceAreas.length > 0 || specializations.length > 0) && (
                                    <div className="mb-3">
                                        <h4 className="text-xs text-500 font-medium mb-2 mt-0">ADDITIONAL INFO</h4>
                                        <div className="surface-100 border-round p-2">
                                            <div className="grid" style={{ rowGap: '0.75rem' }}>
                                                {serviceAreas.length > 0 && (
                                                    <div className="col-12 md:col-6">
                                                        <div className="text-xs text-500 mb-1">Service Areas</div>
                                                        <div className="flex flex-wrap gap-1">
                                                            {serviceAreas.map((area, i) => (
                                                                <Tag key={i} value={area} severity="secondary" className="text-xs" style={{ fontSize: '0.6875rem', padding: '0.15rem 0.4rem' }} />
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {specializations.length > 0 && (
                                                    <div className="col-12 md:col-6">
                                                        <div className="text-xs text-500 mb-1">Specializations</div>
                                                        <div className="flex flex-wrap gap-1">
                                                            {specializations.map((spec, i) => (
                                                                <Tag key={i} value={spec} severity="success" className="text-xs" style={{ fontSize: '0.6875rem', padding: '0.15rem 0.4rem' }} />
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Agreements */}
                                <div className="form-group mb-0">
                                    <div className={`flex align-items-start gap-2 mb-2 cursor-pointer ${hasError('accuracy') ? 'field-error' : ''}`} onClick={() => setDataAccurate(!dataAccurate)}>
                                        <Checkbox checked={dataAccurate} onChange={(e) => setDataAccurate(e.checked)} />
                                        <div>
                                            <span className="text-700 text-sm">I confirm that all information provided is accurate and I am authorized to offer these rates</span>
                                            {hasError('accuracy') && <div className="form-error">{hasError('accuracy')}</div>}
                                        </div>
                                    </div>
                                    <div className={`flex align-items-start gap-2 cursor-pointer ${hasError('terms') ? 'field-error' : ''}`} onClick={() => setTermsAccepted(!termsAccepted)}>
                                        <Checkbox checked={termsAccepted} onChange={(e) => setTermsAccepted(e.checked)} />
                                        <div>
                                            <span className="text-700 text-sm">I agree to the DSA marketplace terms and conditions</span>
                                            {hasError('terms') && <div className="form-error">{hasError('terms')}</div>}
                                        </div>
                                    </div>
                                </div>

                                <Message
                                    severity="info"
                                    className="mt-3 w-full"
                                    text="Your offer will be visible to Lead Owners who can then invite you to their leads. You can pause or edit your offer at any time."
                                />
                            </div>
                        </>
                    )}

                    {/* Navigation Buttons */}
                    <div className="flex justify-content-between">
                        <Button
                            label="Back"
                            icon="pi pi-arrow-left"
                            className="p-button-text"
                            onClick={prevStep}
                            disabled={activeStep === 0}
                        />
                        {activeStep < 1 ? (
                            <Button
                                label="Continue"
                                icon="pi pi-arrow-right"
                                iconPos="right"
                                onClick={nextStep}
                            />
                        ) : (
                            <Button
                                label={isEditMode ? 'Update Offer' : 'Publish Offer'}
                                icon="pi pi-check"
                                onClick={handlePost}
                                loading={posting}
                                data-testid="submit_button"
                            />
                        )}
                    </div>
                </div>

                {/* Summary Sidebar */}
                <div className="col-12 lg:col-4">
                    <SummarySidebar />
                </div>
            </div>
        </div>
    );
};

export default PostOffer;
